import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import HeroDemo from './HeroDemo';
import FeedbackButton from './FeedbackButton';

const roles = [
  'Business Analysts',
  'Lawyers',
  'Researchers',
  'Product Teams',
  'Sales Teams',
  'Data Scientists'
];

function LandingPage() {
  const { user } = useAuth();
  const authLink = user ? '/app' : '/auth';
  const [rotatingRole, setRotatingRole] = useState('Business Analysts');

  useEffect(() => {
    const interval = setInterval(() => {
      setRotatingRole(prev => {
        const currentIndex = roles.indexOf(prev);
        return roles[(currentIndex + 1) % roles.length];
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-cream-100">
      {/* Navigation Bar */}
      <nav className="border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/assets/logo_2.png" alt="QuickSheet Logo" className="h-8 w-8" />
            <span className="font-bold text-xl text-navy-900">QuickSheet</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="#features" className="hidden md:inline text-gray-600 hover:text-navy-900 transition">Features</a>
            <Link to="/pricing" className="hidden md:inline text-gray-600 hover:text-navy-900 transition">Pricing</Link>
            {!user && (
              <Link to="/auth?mode=signin" className="text-gray-600 hover:text-navy-900 transition font-medium">
                Sign In
              </Link>
            )}
            <Link
              to={authLink}
              className="px-5 py-2.5 bg-copper-500 text-white font-medium rounded-lg hover:bg-copper-600 transition-all"
            >
              {user ? 'Go to App' : 'Get Started'}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 py-16 md:py-24 text-center">
        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-500 mb-6">
          QuickSheet — The smarter spreadsheet viewer
        </p>
        <h1 className="text-5xl md:text-7xl font-serif text-navy-900 mb-8 leading-tight max-w-4xl mx-auto">
          A clean, beautiful way to{' '}
          <span className="text-copper-500 italic">
            view, analyze and present
          </span>{' '}
          your spreadsheet data
        </h1>

        <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-2xl mx-auto">
          No cut-off texts, No clunky interface, Full data privacy.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-wrap justify-center gap-3 mb-6">
          <Link
            to={authLink}
            className="px-8 py-4 bg-copper-500 text-white font-medium text-lg rounded-lg hover:bg-copper-600 transition-all duration-200 inline-flex items-center gap-2"
          >
            Get Started Free
          </Link>
          <a
            href="#features"
            className="px-8 py-4 bg-navy-900 text-white font-medium text-lg rounded-lg hover:bg-navy-800 transition-all duration-200 inline-flex items-center gap-2"
          >
            See Features
          </a>
        </div>

        {/* Trust Section */}
        <p className="text-gray-500 text-sm mb-12">
          Used and loved by{' '}
          <span className="font-semibold text-navy-900 transition-all duration-500">
            {rotatingRole}
          </span>
        </p>

        {/* Hero Demo */}
        <HeroDemo />
      </section>

      {/* Value Add Section */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-12 md:py-16">
        <div className="text-center mb-16">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-500 mb-4">Why QuickSheet</p>
          <h2 className="text-4xl md:text-5xl font-serif text-navy-900 mb-4">The outcomes that <span className="text-copper-500 italic">matter</span></h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Value 1 */}
          <div className="bg-cream-50 border border-cream-300 rounded-lg p-8 flex flex-col">
            <img src="/assets/full_data_privacy.jpg" alt="Full data privacy" className="w-3/5 mx-auto rounded-lg mb-4" />
            <h3 className="text-xl font-serif font-bold text-navy-900 mb-4">Full data privacy</h3>
            <ul className="text-gray-600 text-base space-y-2">
              <li>Full control over what stays local and what's uploaded</li>
              <li>No data collection or tracking without consent</li>
              <li>Complete control over your sensitive data</li>
            </ul>
          </div>

          {/* Value 2 */}
          <div className="bg-cream-50 border border-cream-300 rounded-lg p-8 flex flex-col">
            <img src="/assets/telescope.jpg" alt="Complete visibility" className="w-3/5 mx-auto rounded-lg mb-4" />
            <h3 className="text-xl font-serif font-bold text-navy-900 mb-4">Complete data visibility</h3>
            <ul className="text-gray-600 text-base space-y-2">
              <li>See full text without truncation or limits</li>
              <li>Find patterns and insights faster</li>
              <li>Make decisions with complete context</li>
            </ul>
          </div>

          {/* Value 3 */}
          <div className="bg-cream-50 border border-cream-300 rounded-lg p-8 flex flex-col">
            <img src="/assets/bullseye.jpg" alt="Present with clarity" className="w-3/5 mx-auto rounded-lg mb-4" />
            <h3 className="text-xl font-serif font-bold text-navy-900 mb-4">Present data with clarity</h3>
            <ul className="text-gray-600 text-base space-y-2">
              <li>Communicate insights with crystal clarity</li>
              <li>Win over stakeholders with polished visuals</li>
              <li>Close the sale with message clarity</li>
            </ul>
          </div>

          {/* Value 4 */}
          <div className="bg-cream-50 border border-cream-300 rounded-lg p-8 flex flex-col">
            <img src="/assets/perspective.jpg" alt="Switch perspectives" className="w-3/5 mx-auto rounded-lg mb-4" />
            <h3 className="text-xl font-serif font-bold text-navy-900 mb-4">Switch perspectives instantly</h3>
            <ul className="text-gray-600 text-base space-y-2">
              <li>Create views for sales, support, and leadership</li>
              <li>Switch between them instantly—no manual filtering</li>
              <li>One spreadsheet, infinite perspectives</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="max-w-7xl mx-auto px-6 py-12 md:py-16">
        <div className="text-center mb-16">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-500 mb-4">Use Cases</p>
          <h2 className="text-4xl md:text-5xl font-serif text-navy-900 mb-4">Who's Using <span className="text-copper-500 italic">QuickSheet</span></h2>
          <p className="text-lg text-gray-600">See how different teams benefit every day</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { title: 'Business Analysts', desc: 'Review reports with full text visibility without character limits.' },
            { title: 'Lawyers', desc: 'Review legal excerpts and contracts in full.' },
            { title: 'Researchers', desc: 'Analyze survey results and research data without cutoffs.' },
            { title: 'Product Teams', desc: 'Review user feedback and collaborate on insights.' },
            { title: 'Sales Teams', desc: 'Present customer data clearly and close deals.' },
            { title: 'DevOps Engineers', desc: 'Review log files and system data in full.' },
          ].map((useCase, idx) => (
            <div key={idx} className="bg-cream-50 rounded-lg p-6 border border-cream-300 hover:border-copper-400 transition-colors duration-300">
              <h3 className="text-lg font-serif font-semibold text-navy-900 mb-2">
                {useCase.title}
              </h3>
              <p className="text-gray-600">{useCase.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="max-w-7xl mx-auto px-6 py-12 md:py-16">
        <div className="text-center mb-16">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-500 mb-4">Testimonials</p>
          <h2 className="text-4xl md:text-5xl font-serif text-navy-900 mb-4">Loved by our <span className="text-copper-500 italic">beta users</span></h2>
          <p className="text-lg text-gray-600">Join hundreds who've already experienced the difference</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              quote: "We were drowning in customer feedback spreadsheets. Half the text was cut off, so we'd miss important patterns. QuickSheet lets us see every word in context. We now spot insights 5x faster and have cut review time from 8 hours to 1.5 hours per week.",
              name: 'Maria Gonzalez',
              title: 'Head of Customer Insights, SaaS Company',
            },
            {
              quote: "During pitches, I'd share spreadsheets with prospects, but they'd ask why columns were cut off. Killed the momentum. With QuickSheet, I share beautiful, complete data views in seconds. Already closed 3 deals this month because decision-makers could finally see everything.",
              name: 'James Wei',
              title: 'Enterprise Sales Rep, B2B SaaS',
            },
            {
              quote: "We needed one master spreadsheet for sales, support, and exec dashboards—but different teams need different views. QuickSheet switches perspectives instantly. Plus, everything stays local on our devices, which is crucial for handling sensitive client data without compromise.",
              name: 'Lisa Anderson',
              title: 'Operations Director, Professional Services',
            },
          ].map((testimonial, idx) => (
            <div key={idx} className="bg-cream-50 border border-cream-300 rounded-lg p-8">
              <div className="flex items-center gap-1 mb-4">
                <span className="text-copper-500">&#9733;&#9733;&#9733;&#9733;&#9733;</span>
              </div>
              <p className="text-gray-700 mb-6 italic font-serif">"{testimonial.quote}"</p>
              <p className="font-semibold text-navy-900">{testimonial.name}</p>
              <p className="text-sm text-gray-500">{testimonial.title}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Before & After Section */}
      <section className="max-w-7xl mx-auto px-6 py-12 md:py-16">
        <div className="text-center mb-16">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-500 mb-4">Comparison</p>
          <h2 className="text-4xl md:text-5xl font-serif text-navy-900 mb-4">See the <span className="text-copper-500 italic">Difference</span></h2>
          <p className="text-lg text-gray-600">Traditional spreadsheet vs QuickSheet</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Before */}
          <div>
            <div className="mb-6 text-center">
              <h3 className="text-2xl font-serif font-bold text-navy-900 mb-2">Standard Spreadsheet</h3>
              <p className="text-gray-600">Text gets cut off. Data is hard to read.</p>
            </div>
            <img src="/assets/before_spreadsheet.jpg" alt="Standard Spreadsheet" className="w-full rounded-lg border-2 border-cream-300" />
          </div>

          {/* After */}
          <div>
            <div className="mb-6 text-center">
              <h3 className="text-2xl font-serif font-bold text-navy-900 mb-2">QuickSheet</h3>
              <p className="text-gray-600">Complete visibility. Beautiful formatting.</p>
            </div>
            <div className="rounded-lg border border-cream-300 overflow-hidden shadow-sm">
              {/* Header */}
              <div className="grid bg-copper-500" style={{ gridTemplateColumns: '100px 1fr 70px' }}>
                <div className="px-3 py-2 text-xs font-semibold text-white uppercase tracking-wide">Name</div>
                <div className="px-3 py-2 text-xs font-semibold text-white uppercase tracking-wide">Feedback</div>
                <div className="px-3 py-2 text-xs font-semibold text-white uppercase tracking-wide">Status</div>
              </div>
              {/* Rows */}
              {[
                { name: 'Sarah Chen', feedback: 'Dashboard loading time has improved significantly since the last update — pages that used to take 3-4 seconds now render almost instantly. Would love to see dark mode support added in the next release, especially for users who work late hours reviewing data. This would also help reduce eye strain during long analysis sessions.', status: 'Open', statusColor: 'bg-amber-100 text-amber-800' },
                { name: 'James Rivera', feedback: 'The export to PDF feature works great for client reports and has saved us a ton of manual formatting work. One suggestion: allow custom headers and footers in the exported documents so we can add our company branding and legal disclaimers without having to edit the PDF manually after every single export.', status: 'Review', statusColor: 'bg-blue-100 text-blue-800' },
                { name: 'Priya Patel', feedback: 'Search across all columns is incredibly fast now. Previously it took several seconds with large datasets of 10k+ rows, but now it feels instant even on our biggest spreadsheets. This has been a game-changer for our research team during quarterly reviews when we need to cross-reference multiple data points quickly.', status: 'Closed', statusColor: 'bg-emerald-100 text-emerald-700' },
                { name: 'Marcus Johnson', feedback: 'Mobile layout needs work — the table columns overlap on smaller screens and some text gets completely hidden behind other columns. Consider a card-based layout for mobile users, or at minimum allow horizontal scrolling with a fixed first column so people can always see which row they are looking at.', status: 'Open', statusColor: 'bg-amber-100 text-amber-800' },
              ].map((row, idx) => (
                <div key={idx} className="grid items-start border-b border-cream-200 bg-cream-50" style={{ gridTemplateColumns: '100px 1fr 70px' }}>
                  <div className="px-3 py-2.5 text-sm text-navy-900 font-medium">{row.name}</div>
                  <div className="px-3 py-2.5 text-sm text-gray-700 leading-relaxed">{row.feedback}</div>
                  <div className="px-3 py-2.5">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${row.statusColor}`}>{row.status}</span>
                  </div>
                </div>
              ))}
              {/* Pagination */}
              <div className="flex items-center justify-between px-3 py-2 bg-cream-100 text-xs text-gray-500">
                <span>Showing 4 of 24 rows</span>
                <div className="flex gap-1">
                  <span className="px-2 py-0.5 rounded bg-copper-500 text-white font-medium">1</span>
                  <span className="px-2 py-0.5 rounded text-gray-500">2</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-6 py-12 md:py-16 text-center">
        <h2 className="text-4xl md:text-5xl font-serif text-navy-900 mb-6">
          Ready to transform how you <span className="text-copper-500 italic">work with data</span>?
        </h2>
        <p className="text-lg text-gray-600 mb-8">
          Join thousands of professionals who've already made the switch
        </p>
        <Link
          to={authLink}
          className="px-10 py-4 bg-copper-500 text-white font-medium text-lg rounded-lg hover:bg-copper-600 transition-all duration-200 inline-flex items-center gap-2"
        >
          Get Started Free
        </Link>
      </section>

      {/* Footer */}
      <footer className="bg-navy-900 text-gray-300">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/assets/logo_2.png" alt="QuickSheet Logo" className="h-8 w-8" />
                <span className="font-bold text-xl text-white">QuickSheet</span>
              </div>
            </div>

            {/* Product */}
            <div>
              <h3 className="font-semibold text-white mb-4">Product</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="text-gray-400 hover:text-white transition">Features</a></li>
                <li><Link to="/pricing" className="text-gray-400 hover:text-white transition">Pricing</Link></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="font-semibold text-white mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="text-gray-400 hover:text-white transition">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-white transition">Terms of Service</Link></li>
              </ul>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-800 pt-8">
            <p className="text-sm text-gray-400">&copy; 2026 QuickSheet. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Feedback Floating Button */}
      <div className="fixed bottom-6 right-6 z-40">
        <FeedbackButton user={user} floating />
      </div>
    </div>
  );
}

export default LandingPage;
